import { useState } from "react";
import { RevealSection } from "../motion/RevealSection";
import { resolveMediaUrl } from "../../lib/api";
import { PlayCircle } from "lucide-react";

function getEmbedUrl(url) {
  try {
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const idMatch = url.match(/(?:v=|\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
      if (idMatch) return { embed: `https://www.youtube.com/embed/${idMatch[1]}?autoplay=1`, type: "iframe" };
    }
    if (url.includes("vimeo.com")) {
      const idMatch = url.match(/vimeo\.com\/(\d+)/);
      if (idMatch) return { embed: `https://player.vimeo.com/video/${idMatch[1]}?autoplay=1`, type: "iframe" };
    }
  } catch {
    // unrecognized video URL format — fall back to a plain link
  }
  return { embed: url, type: "link" };
}

const VideoCard = ({ v, idx }) => {
  const [playing, setPlaying] = useState(false);
  const isFile = v.type === "file";
  const { embed, type } = isFile ? {} : getEmbedUrl(v.url);
  const showThumbnail = v.thumbnail && !playing && type !== "link";

  return (
    <RevealSection delay={idx * 0.1} className="group">
      <div data-testid={`video-card-${idx}`} className="relative border border-white/10 overflow-hidden">
        {showThumbnail ? (
          <button
            data-testid={`video-thumbnail-play-${idx}`}
            onClick={() => setPlaying(true)}
            className="relative block w-full aspect-video overflow-hidden"
          >
            <img
              src={resolveMediaUrl(v.thumbnail)}
              alt={v.title || `Video ${idx + 1}`}
              className="absolute inset-0 h-full w-full object-cover brightness-75 group-hover:brightness-90 transition-all duration-500"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-twp-gold text-black group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <PlayCircle size={32} className="text-black" />
              </span>
            </div>
          </button>
        ) : isFile ? (
          <video controls autoPlay={playing} poster={v.thumbnail ? resolveMediaUrl(v.thumbnail) : undefined} className="w-full aspect-video bg-black" data-testid={`video-player-${idx}`}>
            <source src={resolveMediaUrl(v.url)} />
          </video>
        ) : type === "iframe" ? (
          <div className="aspect-video w-full">
            <iframe
              src={embed}
              title={v.title || `Video ${idx + 1}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <a
            href={v.url}
            target="_blank"
            rel="noopener noreferrer"
            className="relative flex aspect-video w-full items-center justify-center bg-black/40 hover:bg-black/60 transition-colors overflow-hidden"
          >
            {v.thumbnail && (
              <img src={resolveMediaUrl(v.thumbnail)} alt="" className="absolute inset-0 h-full w-full object-cover brightness-60" />
            )}
            <PlayCircle className="relative text-twp-gold" size={48} />
          </a>
        )}
        {v.title && <p className="p-4 text-white/80 text-sm font-serif-display text-lg">{v.title}</p>}
      </div>
    </RevealSection>
  );
};

export const Videos = ({ content }) => {
  const videos = content?.videos || [];

  return (
    <section id="videos" className="relative bg-twp-charcoal py-24 md:py-36 px-6 md:px-16">
      <RevealSection>
        <span className="text-twp-gold text-xs uppercase tracking-[0.35em]">04 — Videos Destacados</span>
        <h2 className="font-serif-display text-4xl md:text-6xl text-white mt-4 mb-16 max-w-2xl">
          El movimiento detrás de cada historia
        </h2>
      </RevealSection>

      {videos.length === 0 ? (
        <p data-testid="videos-empty-state" className="text-white/50 text-sm">
          Próximamente compartiremos aquí nuestros videos destacados.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {videos.map((v, idx) => (
            <VideoCard key={`${v.url}-${idx}`} v={v} idx={idx} />
          ))}
        </div>
      )}
    </section>
  );
};
